import React, { useEffect, useRef, useState } from "react";
import { Loader2, Copy, GitFork, RotateCw, Check, Sparkles, CheckCircle2, AlertTriangle, ThumbsUp, ThumbsDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

function renderMarkdown(text) {
  if (!text) return { __html: "" };
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  html = html.replace(/```([\s\S]*?)```/g, (_, code) => {
    const c = code.replace(/^[a-zA-Z0-9_-]*\n?/, "");
    return `<pre><code>${c.trim()}</code></pre>`;
  });
  html = html.replace(/`([^`\n]+)`/g, "<code>$1</code>");
  html = html.replace(/^###\s+(.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^##\s+(.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^#\s+(.+)$/gm, "<h1>$1</h1>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  html = html.replace(/\[([^\]]+)\]\((https?:[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/(^|\n)- (.+)/g, "$1<li>$2</li>");
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m.replace(/\n/g, "")}</ul>`);
  html = html.split(/\n{2,}/).map((p) => (p.startsWith("<") ? p : `<p>${p.replace(/\n/g, "<br/>")}</p>`)).join("");
  return { __html: html };
}

export default function ChatThread({ messages, streaming, streamText, modelName, onRegenerate, onFork, conversationId, fileIds }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamText]);

  // Find the index of the most recent assistant message — only that one gets Regenerate.
  const lastAssistantIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  })();

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="chat-thread">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8">
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id}
            message={m}
            isLastAssistant={i === lastAssistantIdx}
            canRegenerate={!!onRegenerate && !streaming}
            onRegenerate={onRegenerate}
            onFork={onFork}
            conversationId={conversationId}
            fileIds={fileIds}
          />
        ))}

        {streaming && (
          <div className="my-6">
            <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
              {modelName || "Nexa"}
            </div>
            <div className="bg-secondary/50 border border-border p-4 rounded-sm">
              <div
                className="prose-chat"
                dangerouslySetInnerHTML={renderMarkdown(streamText || "")}
              />
              {!streamText && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…
                </div>
              )}
              {streamText && <span className="inline-block w-2 h-4 bg-foreground ml-0.5 animate-blink" />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto-inspect bubble — Anthropic/Claude-style progress + streamed answer.
// Renders an eyebrow, the live phase list, and the LLM's inspection text.
// ---------------------------------------------------------------------------
const PHASE_LABELS = {
  queued: { es: "En cola", en: "Queued" },
  uploading: { es: "Subiendo a Tensornex", en: "Uploading to Tensornex" },
  downloading_media: { es: "Descargando media", en: "Downloading media" },
  extracting_meta: { es: "Leyendo estructura", en: "Reading structure" },
  converting: { es: "Extrayendo texto", en: "Extracting text" },
  vlm_processing: { es: "Analizando imagen (VLM)", en: "Running VLM" },
  transcribing: { es: "Transcribiendo", en: "Transcribing" },
  chunking: { es: "Dividiendo en bloques", en: "Chunking" },
  embedding: { es: "Indexando para RAG", en: "Embedding for RAG" },
  processing: { es: "Procesando", en: "Processing" },
  ready: { es: "Listo para inspeccionar", en: "Ready to inspect" },
  thinking: { es: "Pensando", en: "Thinking" },
  cached: { es: "Inspección previa cacheada", en: "Cached prior inspection" },
};

function phaseLabel(stage, lang) {
  const m = PHASE_LABELS[stage];
  if (!m) return stage;
  return lang?.startsWith("en") ? m.en : m.es;
}

function InspectBubble({ message }) {
  const { t, i18n } = useTranslation();
  const phases = message.meta?.phases || [];
  const streaming = !!message.meta?.streaming;
  const error = message.meta?.error;
  const filename = message.meta?.filename;
  // Problem 1 — admin-configured skip notice (low balance / no content) and
  // cached-from-other-conv label, both rendered inline in the bubble.
  const skipMessage = message.meta?.skip_message;
  const skipReason = message.meta?.skip_reason;
  const cachedLabel = message.meta?.cached_label || message.meta?.cached_from;
  // Dedup consecutive phases with the same stage — keep the LATEST entry
  // per stage so heartbeat updates (e.g. waited_seconds growing) show fresh
  // values to the user instead of stale ones from the first event.
  const latestByStage = new Map();
  phases.forEach((p) => {
    if (p?.stage) latestByStage.set(p.stage, p);
  });
  const visiblePhases = Array.from(latestByStage.values());
  const lastStage = phases[phases.length - 1]?.stage;
  return (
    <div className="my-6 group" data-testid={`message-inspect-${message.id}`}>
      <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <Sparkles className="h-3 w-3 text-primary" />
        {t("workspace:inspect.eyebrow", "Auto-inspección")}
        {filename && <span className="text-muted-foreground">· {filename}</span>}
      </div>
      <div className="border border-primary/30 bg-primary/[0.04] p-4 rounded-sm space-y-3">
        {cachedLabel && (
          <div
            className="text-xs font-mono text-muted-foreground italic"
            data-testid={`inspect-cached-label-${message.id}`}
          >
            {message.meta?.cached_label || cachedLabel}
          </div>
        )}
        {skipMessage && (
          <div
            className="flex items-start gap-2 text-sm"
            data-testid={`inspect-skip-${message.id}`}
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <span>{skipMessage}</span>
          </div>
        )}
        {!skipMessage && visiblePhases.length > 0 && (
          <ul className="space-y-1 text-xs font-mono" data-testid={`inspect-phases-${message.id}`}>
            {visiblePhases.map((p, idx) => {
              const isLast = streaming && p.stage === lastStage && !message.content;
              return (
                <li key={p.stage + idx} className="flex items-center gap-2">
                  {isLast ? (
                    <Loader2 className="h-3 w-3 animate-spin text-primary shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
                  )}
                  <span className="text-muted-foreground">
                    {phaseLabel(p.stage, i18n.language)}
                    {typeof p.progress_pct === "number" && p.progress_pct > 0 && p.progress_pct < 100 && (
                      <span> · {p.progress_pct}%</span>
                    )}
                    {/* Fix A — surface long-running Tensornex retries inline so the bubble
                        never looks frozen during the 2-7 minute wait the upstream pipeline
                        sometimes takes (transient retry) */}
                    {isLast && typeof p.waited_seconds === "number" && p.waited_seconds >= 30 && (
                      <span> · {i18n.language?.startsWith("en")
                        ? `${Math.round(p.waited_seconds / 60)} min waiting`
                        : `${Math.round(p.waited_seconds / 60)} min esperando`}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {error && (
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {(message.content || streaming) && !error && !skipMessage && (
          <div className="border-t border-primary/20 pt-3">
            <div
              className="prose-chat"
              dangerouslySetInnerHTML={renderMarkdown(message.content || "")}
            />
            {streaming && message.content && (
              <span className="inline-block w-2 h-4 bg-foreground ml-0.5 animate-blink" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ message, isLastAssistant, canRegenerate, onRegenerate, onFork, conversationId, fileIds }) {
  const isUser = message.role === "user";
  const isInspect = message.meta?.type === "auto_inspect";
  const [copied, setCopied] = useState(false);
  // Phase 6.2 — user feedback state
  const [rating, setRating] = useState(message.user_rating || null);   // +1 / -1 / null
  const [submittingFeedback, setSubmittingFeedback] = useState(null);  // 1 | -1 | null
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");
  // Fetch existing rating on mount for assistant messages (so refresh keeps state)
  useEffect(() => {
    if (isUser || isInspect || !conversationId || !message.id || rating !== null) return;
    let cancelled = false;
    api.get(`/conversations/${conversationId}/messages/${message.id}/feedback`)
      .then(({ data }) => { if (!cancelled && data?.rating) setRating(data.rating); })
      .catch(() => { /* silent — endpoint returns null for never-rated messages */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, message.id]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const submitFeedback = async (newRating, withComment = null) => {
    if (!conversationId || !message.id) return;
    setSubmittingFeedback(newRating);
    try {
      await api.post(`/conversations/${conversationId}/messages/${message.id}/feedback`, {
        rating: newRating,
        comment: withComment || null,
        file_ids: fileIds || [],
      });
      setRating(newRating);
      setShowCommentBox(false);
      setComment("");
      toast.success(newRating === 1 ? "Thanks for the feedback!" : "Thanks — we'll look into it.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save feedback");
    } finally {
      setSubmittingFeedback(null);
    }
  };

  const handleThumbUp = () => submitFeedback(1);
  const handleThumbDown = () => {
    // First click opens the comment box. Second click (or submit) sends.
    if (!showCommentBox && rating !== -1) {
      setShowCommentBox(true);
      return;
    }
    submitFeedback(-1, comment);
  };

  if (isInspect) {
    return <InspectBubble message={message} />;
  }

  return (
    <div className="my-6 group" data-testid={`message-${message.role}`}>
      <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
        {isUser ? "You" : "Nexa"} {message.via === "voice" && <span className="text-primary">· voice</span>}
      </div>
      <div className={isUser
        ? "border border-primary/30 bg-primary/5 p-4 rounded-sm"
        : "bg-secondary/50 border border-border p-4 rounded-sm"
      }>
        <div className="prose-chat" dangerouslySetInnerHTML={renderMarkdown(message.content || "")} />
      </div>
      {/* Hover actions */}
      <div className="mt-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition" data-testid={`message-actions-${message.id}`}>
        <button
          onClick={handleCopy}
          className="text-[11px] font-mono text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded-sm hover:bg-secondary transition flex items-center gap-1"
          data-testid={`msg-copy-${message.id}`}
          title="Copy message"
        >
          {copied ? <Check className="h-3 w-3 text-primary" /> : <Copy className="h-3 w-3" />}
          {copied ? "copied" : "copy"}
        </button>
        {!isUser && isLastAssistant && canRegenerate && (
          <button
            onClick={() => onRegenerate?.(message)}
            className="text-[11px] font-mono text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded-sm hover:bg-secondary transition flex items-center gap-1"
            data-testid={`msg-regen-${message.id}`}
            title="Regenerate response"
          >
            <RotateCw className="h-3 w-3" /> regenerate
          </button>
        )}
        {onFork && (
          <button
            onClick={() => onFork?.(message)}
            className="text-[11px] font-mono text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded-sm hover:bg-secondary transition flex items-center gap-1"
            data-testid={`msg-fork-${message.id}`}
            title="Start a new chat from here"
          >
            <GitFork className="h-3 w-3" /> fork
          </button>
        )}
        {/* Phase 6.2 — thumbs feedback on assistant messages only */}
        {!isUser && conversationId && message.id && (
          <>
            <button
              onClick={handleThumbUp}
              disabled={submittingFeedback !== null}
              className={`text-[11px] font-mono px-1.5 py-0.5 rounded-sm transition flex items-center gap-1 ${
                rating === 1
                  ? "text-emerald-700 dark:text-emerald-300 bg-emerald-100/50 dark:bg-emerald-900/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
              data-testid={`msg-thumbs-up-${message.id}`}
              title="Helpful answer"
            >
              <ThumbsUp className="h-3 w-3" /> {rating === 1 ? "rated" : "good"}
            </button>
            <button
              onClick={handleThumbDown}
              disabled={submittingFeedback !== null}
              className={`text-[11px] font-mono px-1.5 py-0.5 rounded-sm transition flex items-center gap-1 ${
                rating === -1
                  ? "text-red-700 dark:text-red-300 bg-red-100/50 dark:bg-red-900/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
              data-testid={`msg-thumbs-down-${message.id}`}
              title="Not helpful"
            >
              <ThumbsDown className="h-3 w-3" /> {rating === -1 ? "rated" : "bad"}
            </button>
          </>
        )}
      </div>
      {/* Optional comment box on thumbs-down */}
      {showCommentBox && (
        <div className="mt-2 ml-1" data-testid={`feedback-comment-box-${message.id}`}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 200))}
            placeholder="What was wrong? (optional, max 200 chars)"
            className="w-full text-xs p-2 rounded-sm border border-border bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            rows={2}
            maxLength={200}
            autoFocus
            data-testid={`feedback-comment-${message.id}`}
            onKeyDown={(e) => {
              if (e.key === "Escape") { setShowCommentBox(false); setComment(""); }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitFeedback(-1, comment);
            }}
          />
          <div className="flex items-center gap-2 mt-1">
            <button
              onClick={() => submitFeedback(-1, comment)}
              disabled={submittingFeedback !== null}
              className="text-[11px] font-mono px-2 py-0.5 rounded-sm bg-foreground text-background hover:opacity-80 transition"
              data-testid={`feedback-submit-${message.id}`}
            >
              {submittingFeedback === -1 ? "Sending…" : "Submit"}
            </button>
            <button
              onClick={() => { setShowCommentBox(false); setComment(""); submitFeedback(-1, null); }}
              className="text-[11px] font-mono px-2 py-0.5 rounded-sm text-muted-foreground hover:text-foreground transition"
              data-testid={`feedback-skip-${message.id}`}
            >
              Skip
            </button>
            <span className="text-[10px] text-muted-foreground ml-auto">{comment.length}/200</span>
          </div>
        </div>
      )}
    </div>
  );
}
